import React, { useMemo, useState } from 'react'
import { CheckCircle2, CircleHelp, Copy, GitCompare, Maximize2, ScanSearch } from 'lucide-react'
import styles from './PromptAnalyzerPanel.module.css'
import { analyzePrompt } from './promptAnalyzer'
import { copyText } from './clipboard'
import SegmentCompareModal from './SegmentCompareModal'
import { AnalyzedSegment, PromptAnalyzerPanelProps } from './types'

const PromptAnalyzerPanel: React.FC<PromptAnalyzerPanelProps> = ({ templates, onJump }) => {
  const [input, setInput] = useState('')
  const [analyzedText, setAnalyzedText] = useState<string | null>(null)
  const [openIndex, setOpenIndex] = useState<number | null>(null)

  // Re-runs when templates are edited, so fixing a prompt turns its section green live
  const analysis = useMemo(
    () => (analyzedText ? analyzePrompt(analyzedText, templates) : null),
    [analyzedText, templates],
  )

  const runAnalysis = () => {
    setOpenIndex(null)
    setAnalyzedText(input.trim() ? input : null)
  }

  const templateContent = (key: string) => templates.find((t) => t.key === key)?.content ?? ''
  const openSegment = openIndex !== null ? analysis?.segments[openIndex] : undefined

  const renderSegment = (seg: AnalyzedSegment, i: number) => {
    const text = seg.text.trim()
    const lineCount = text.split('\n').length
    const kindClass = seg.kind === 'match' ? styles.match : seg.kind === 'similar' ? styles.similar : styles.unknown

    return (
      <section key={i} className={`${styles.segment} ${kindClass}`}>
        <div className={styles.segmentHeader}>
          {seg.kind === 'match' && <CheckCircle2 size={14} />}
          {seg.kind === 'similar' && <GitCompare size={14} />}
          {seg.kind === 'unknown' && <CircleHelp size={14} />}
          <span className={styles.segmentTitle}>
            {seg.kind === 'unknown' ? 'Not in any prompt' : (
              <>
                {seg.kind === 'similar' && 'Similar to '}
                <button type="button" className={styles.templateLink} onClick={() => onJump(seg.key)} title="Go to this prompt">
                  {seg.label}
                </button>
                {seg.kind === 'similar' && ` (${Math.round(seg.similarity * 100)}%)`}
              </>
            )}
            {' '}
            <span className={styles.segmentMeta}>
              · {lineCount} {lineCount === 1 ? 'line' : 'lines'}, {text.length.toLocaleString()} chars
            </span>
          </span>
          <div className={styles.headerActions}>
            <button type="button" className={styles.segmentBtn} onClick={() => copyText(text)} title="Copy this section">
              <Copy size={12} /> Copy
            </button>
            <button
              type="button"
              className={styles.segmentBtn}
              onClick={() => setOpenIndex(i)}
              title={seg.kind === 'similar' ? 'Compare with the saved prompt side by side' : 'Open in a popup'}
            >
              <Maximize2 size={12} /> {seg.kind === 'similar' ? 'Compare' : 'Open'}
            </button>
          </div>
        </div>
        <pre className={styles.segmentText} onClick={() => setOpenIndex(i)}>{text}</pre>
      </section>
    )
  }

  return (
    <div className={styles.panel}>
      <div className={styles.inputArea}>
        <textarea
          className={styles.input}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Paste a full prompt (e.g. the system prompt a chat actually sent) to break it down into the defined prompts…"
          rows={10}
          spellCheck={false}
        />
        <div className={styles.inputActions}>
          {analyzedText && (
            <button type="button" className={styles.clearBtn} onClick={() => { setInput(''); setAnalyzedText(null) }}>
              Clear
            </button>
          )}
          <button type="button" className={styles.analyzeBtn} onClick={runAnalysis} disabled={!input.trim()}>
            <ScanSearch size={14} />
            Analyze
          </button>
        </div>
      </div>

      {analysis && (
        <>
          <div className={styles.summary}>
            <span className={styles.chipMatch}>{analysis.matchedCount} matched</span>
            <span className={styles.chipSimilar}>{analysis.similarCount} similar</span>
            <span className={styles.chipUnknown}>{analysis.unknownChars.toLocaleString()} chars not in any prompt</span>
          </div>
          <div className={styles.segments}>
            {analysis.segments.length === 0
              ? <p className={styles.empty}>Nothing to analyze.</p>
              : analysis.segments.map(renderSegment)}
          </div>
        </>
      )}

      {openSegment && (
        <SegmentCompareModal
          segment={openSegment}
          sourceContent={openSegment.kind === 'unknown' ? null : templateContent(openSegment.key)}
          onClose={() => setOpenIndex(null)}
          onJump={onJump}
        />
      )}
    </div>
  )
}

export default PromptAnalyzerPanel
